import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import {
  CalendarOptions,
  DatesSetArg,
  EventClickArg,
  EventInput,
  DateSelectArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { ProfessorAcademicAppointmentService } from '../../../../core/services/academic-appointments/professor-academic-appointment.service';
import { SessionViewService } from '../../../../core/services/academic-appointments/session-view.service';
import {
  AttendanceVerdict,
  SessionAttendance,
  SessionJoinService,
} from '../../../../core/services/academic-appointments/session-join.service';
import {
  AcademicAppointment,
  AcademicSchedulingOptions,
  CreateAcademicAppointmentDto,
  SESSION_KIND_VALUE,
  SESSION_KINDS,
  SchedulingStudent,
  SchedulingSubject,
  SessionKind,
  UpdateAcademicAppointmentDto,
  displayTime,
  sessionEnd,
  sessionStart,
} from '../../../../core/interfaces/academic-appointment';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { SessionAudienceComponent } from '../../../ui/session-audience/session-audience.component';
import { LocalizedLabelService } from '../../../../core/services/utils/localized-label.service';
import {
  SubjectChoiceService,
  SubjectPathGroup,
} from '../../../../core/services/utils/subject-choice.service';
import {
  UploadPurpose,
  UploadResult,
  UploadService,
} from '../../../../core/services/uploads/upload.service';
import { ProfessorCourseMaterialService } from '../../../../core/services/professor-course-material.service';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

interface SessionForm {
  title: string;
  kind: SessionKind;
  /**
   * Matières traitées, la principale en tête. Un même cours sert souvent
   * plusieurs niveaux : l'anglais donné une fois pour la 6ème primaire, la 9ème
   * de base et le baccalauréat, trois matières distinctes au référentiel.
   */
  subjectIds: number[];
  groupId: number | null;
  studentIds: number[];
  date: string;
  startTime: string;
  durationMinutes: number;
  location: string;
  meetingUrl: string;
  reason: string;
}

/** Durées proposées, en minutes — couvre le créneau court au demi-journée. */
const DURATIONS = [30, 45, 60, 90, 120, 180, 240];

/** Durée d'une séance ordinaire. C'est le format le plus courant à l'emploi du temps. */
const DEFAULT_DURATION_MINUTES = 45;

/** Bornes acceptées par l'API (`[Range(15, 480)]` sur le DTO académique). */
const MIN_DURATION_MINUTES = 15;
const MAX_DURATION_MINUTES = 480;

const EMPTY_FORM = (): SessionForm => ({
  title: '',
  kind: 'Course',
  subjectIds: [],
  groupId: null,
  studentIds: [],
  date: '',
  startTime: '08:00',
  durationMinutes: DEFAULT_DURATION_MINUTES,
  location: '',
  meetingUrl: '',
  reason: '',
});

@Component({
  selector: 'app-teacher-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, FullCalendarModule, ErrorStateComponent, SessionAudienceComponent, LocalizedDatePipe],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css',
})
export class TeacherCalendarComponent implements OnInit {
  private readonly service = inject(ProfessorAcademicAppointmentService);
  private readonly translate = inject(TranslateService);
  private readonly joinService = inject(SessionJoinService);
  private readonly labels = inject(LocalizedLabelService);
  private readonly subjectChoice = inject(SubjectChoiceService);
  private readonly uploads = inject(UploadService);
  private readonly materials = inject(ProfessorCourseMaterialService);
  readonly view = inject(SessionViewService);

  /**
   * Langue active, en signal.
   *
   * Les libellés du référentiel sont traduits à l'affichage : sans cette
   * dépendance, un `computed` qui les compose resterait figé sur la langue en
   * vigueur au premier calcul, et la bascule AR/FR laisserait la liste des
   * matières dans l'ancienne langue jusqu'au prochain rechargement.
   */
  private readonly lang = toSignal(this.translate.onLangChange, { initialValue: null });

  @ViewChild(FullCalendarComponent) private calendar?: FullCalendarComponent;

  /**
   * Les deux panneaux sont des `<dialog>` natifs, ouverts par `showModal()`.
   *
   * Le navigateur les peint alors dans la **couche supérieure** : ils échappent
   * à tout contexte d'empilement, à tout `overflow` et à toute guerre de
   * `z-index` dans la coquille de l'application. C'est ce qui règle
   * définitivement la modale qui s'affichait sous le calendrier — un ancêtre du
   * routeur suffisait à casser son `position: fixed`. On y gagne aussi le
   * piégeage du focus, la fermeture par Échap et l'inertie de l'arrière-plan,
   * qu'il fallait sinon écrire à la main.
   */
  @ViewChild('formDialog') private formDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('detailDialog') private detailDialog?: ElementRef<HTMLDialogElement>;

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errored = signal(false);

  readonly sessions = signal<AcademicAppointment[]>([]);
  readonly options = signal<AcademicSchedulingOptions>({
    subjects: [], groups: [], students: [], professors: [],
  });

  readonly periodLabel = signal('');
  readonly activeView = signal<CalendarView>('dayGridMonth');

  readonly showForm = signal(false);
  readonly editingId = signal<number | null>(null);
  readonly selected = signal<AcademicAppointment | null>(null);
  readonly studentQuery = signal('');

  /** Bilan de présence de la séance ouverte dans le tiroir. */
  readonly attendance = signal<SessionAttendance | null>(null);
  readonly attendanceLoading = signal(false);

  // ─── Issue d'une séance de production ──────────────────────────────
  readonly outcomeSaving = signal(false);
  readonly outcomeError = signal<string | null>(null);
  /** Ouvre la saisie du motif quand le prof déclare « non tenue ». */
  readonly declining = signal(false);
  declineReason = '';

  // ─── Dépôt de l'enregistrement ─────────────────────────────────────
  readonly uploadOpen = signal(false);
  readonly uploadPercent = signal<number | null>(null);
  readonly uploadError = signal<string | null>(null);
  readonly uploadBusy = signal(false);
  recordingFile: File | null = null;
  recordingTitle = '';

  /**
   * Fichier déjà déposé sur le stockage mais pas encore rattaché.
   *
   * Si l'enregistrement en base échoue après un transfert réussi, l'objet est
   * bel et bien en place : le conserver permet de ne réessayer que le
   * rattachement, au lieu de renvoyer 2 Go.
   */
  private pendingUpload: UploadResult | null = null;

  form: SessionForm = EMPTY_FORM();

  readonly kinds = SESSION_KINDS;
  readonly durations = DURATIONS;

  // Filtres — `null` signifie « tout », l'état par défaut.
  filterSubjectId: number | null = null;
  filterGroupId: number | null = null;
  filterStudentId: number | null = null;

  /**
   * Des filtres sont posés. Un accesseur, pas un `computed` : les champs liés
   * par ngModel ne sont pas des signaux, un `computed` n'aurait aucun
   * producteur et resterait figé à sa première valeur — le bouton « Effacer »
   * n'apparaissait jamais.
   */
  get hasFilters(): boolean {
    return this.filterSubjectId !== null || this.filterGroupId !== null || this.filterStudentId !== null;
  }

  /** Séances à venir, pour le panneau latéral. */
  readonly upcoming = computed(() => {
    const now = new Date();
    return this.sessions()
      .filter((s) => new Date(sessionStart(s)) >= now)
      .sort((a, b) => sessionStart(a).localeCompare(sessionStart(b)))
      .slice(0, 6);
  });

  /** Compteurs de la bande de statistiques — calculés sur les séances chargées. */
  readonly stats = computed(() => {
    const list = this.sessions();
    const minutes = list.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0);
    const groups = new Set(list.map((s) => s.groupId).filter((id): id is number => !!id));
    const subjects = new Set(list.map((s) => s.subjectId).filter((id): id is number => !!id));
    return {
      count: list.length,
      hours: Math.round((minutes / 60) * 10) / 10,
      groups: groups.size,
      subjects: subjects.size,
    };
  });

  /** Matières cochées dans le formulaire — pilotent le filtrage de la liste d'élèves. */
  private readonly formSubjectIds = signal<number[]>([]);

  /**
   * Destinataire de la séance en cours de saisie.
   *
   * Deux intentions, pas un champ vide : soit le prof convie des élèves, soit il
   * se réserve le créneau pour enregistrer. L'ancienne version laissait deviner
   * la seconde en ne remplissant rien, ce qui ne se devine pas.
   */
  readonly audienceMode = signal<'students' | 'recording'>('students');

  /** Effectif visé par le formulaire — nourrit l'aperçu. */
  private readonly formTargetCount = signal(0);

  /**
   * Aperçu de la pastille telle qu'elle apparaîtra dans la grille.
   * C'est l'objet que le professeur compose : il le voit se former.
   */
  readonly preview = computed(() => {
    this.lang();

    // La couleur vient de la matière principale — la première cochée. Une
    // séance à trois matières n'a qu'une pastille : la teindre autrement
    // reviendrait à en inventer une quatrième.
    const subjectId = this.formSubjectIds()[0] ?? null;

    const chosen = this.formSubjectIds()
      .map((id) => this.options().subjects.find((s) => s.id === id))
      .filter((s): s is SchedulingSubject => !!s);

    return {
      // Même teinte que dans la grille : la couleur d'une matière est une
      // information, elle doit être calculée au même endroit pour tout le monde.
      colorClass: this.view.colorClass({ subjectId } as AcademicAppointment),
      subject: chosen.map((s) => this.subjectLabel(s)).join(' · '),
      count: this.formTargetCount(),
    };
  });

  /**
   * Élèves proposés au formulaire. Une fois les matières choisies, seuls ceux
   * qui en suivent au moins une sont proposés — sinon la liste affiche tout
   * l'effectif du prof et devient inutilisable. La recherche libre affine
   * ensuite.
   *
   * <p>Au moins une, et non toutes : le propre d'une séance multi-niveaux est
   * de réunir des élèves qui ne suivent pas la même ligne du référentiel. Exiger
   * l'intersection ne proposerait plus personne.</p>
   */
  readonly pickableStudents = computed<SchedulingStudent[]>(() => {
    const query = this.studentQuery().trim().toLowerCase();
    const subjectIds = this.formSubjectIds();

    return this.options().students.filter((s) => {
      if (
        subjectIds.length > 0 &&
        s.subjectIds?.length &&
        !subjectIds.some((id) => s.subjectIds.includes(id))
      ) {
        return false;
      }
      if (query && !s.fullName.toLowerCase().includes(query)) return false;
      return true;
    });
  });

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    locale: this.view.calendarLocale(),
    direction: this.view.isRtl() ? 'rtl' : 'ltr',
    headerToolbar: false,
    height: 'auto',
    firstDay: 1,
    selectable: true,
    dayMaxEvents: 3,
    nowIndicator: true,
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00',
    expandRows: true,
    // Des colonnes disjointes, pas des pastilles superposées.
    //
    // Par défaut FullCalendar double la largeur de chaque colonne parallèle et
    // laisse la suivante recouvrir la précédente à moitié — le style « Google
    // Agenda ». Avec trois séances à huit heures le même jour, les titres
    // s'écrivaient les uns par-dessus les autres : plus rien n'était lisible,
    // et rien ne disait même combien il y en avait.
    slotEventOverlap: false,
    eventDisplay: 'block',
    displayEventTime: false,
    // Chargement piloté par FullCalendar : il rappelle cette fonction à chaque
    // changement de période, ce qui évite de recréer l'objet d'options.
    events: (info, success, failure) => this.fetchEvents(info, success, failure),
    eventClassNames: (arg) => [this.view.colorClass(arg.event.extendedProps as AcademicAppointment)],
    eventContent: (arg) => this.renderEvent(arg.event.extendedProps as AcademicAppointment),
    datesSet: (arg: DatesSetArg) => this.periodLabel.set(arg.view.title),
    select: (arg: DateSelectArg) => this.openCreateAt(arg),
    eventClick: (arg: EventClickArg) =>
      this.openDetail(arg.event.extendedProps as AcademicAppointment),
  };

  ngOnInit(): void {
    this.loadOptions();
  }

  // ─── Navigation ────────────────────────────────────────────────────

  private api() {
    return this.calendar?.getApi();
  }

  prev(): void { this.api()?.prev(); }
  next(): void { this.api()?.next(); }
  today(): void { this.api()?.today(); }

  switchView(view: CalendarView): void {
    this.activeView.set(view);
    this.api()?.changeView(view);
  }

  reload(): void {
    this.errored.set(false);
    this.api()?.refetchEvents();
  }

  // ─── Chargement ────────────────────────────────────────────────────

  private loadOptions(): void {
    this.service.options().subscribe({
      next: (res) => {
        if (res?.data) this.options.set(res.data);
      },
      error: () => {
        // Le référentiel manquant n'empêche pas de consulter le calendrier ;
        // seul le formulaire de création en dépend, et il le signale.
        this.options.set({ subjects: [], groups: [], students: [], professors: [] });
      },
    });
  }

  private fetchEvents(
    info: { startStr: string; endStr: string },
    success: (events: EventInput[]) => void,
    failure: (error: Error) => void,
  ): void {
    this.loading.set(true);
    this.errored.set(false);

    this.service
      .myCalendar({
        from: info.startStr.slice(0, 10),
        to: info.endStr.slice(0, 10),
        subjectId: this.filterSubjectId,
        groupId: this.filterGroupId,
        studentId: this.filterStudentId,
      })
      .subscribe({
        next: (res) => {
          const list = res?.data ?? [];
          this.sessions.set(list);
          this.loading.set(false);
          success(list.map((s) => this.toEvent(s)));
        },
        error: (err) => {
          // Aucune séance de substitution : le calendrier reste vide et l'écran
          // affiche l'état d'erreur.
          this.sessions.set([]);
          this.errored.set(true);
          this.loading.set(false);
          failure(err);
        },
      });
  }

  private toEvent(s: AcademicAppointment): EventInput {
    return {
      id: String(s.id),
      title: s.title,
      start: sessionStart(s),
      end: sessionEnd(s),
      extendedProps: s,
    };
  }

  /**
   * Pastille d'une séance dans la grille.
   *
   * <p>Le nombre de lignes est décidé ici, d'après la <b>durée</b>, et non par
   * une règle de style d'après la hauteur rendue : en vue horaire, la hauteur
   * d'un créneau est proportionnelle à sa durée, et une pastille qui compose
   * quatre lignes dans un créneau de trente minutes déborde quoi qu'on lui
   * dise ensuite. Une tentative précédente s'appuyait sur une requête de
   * conteneur — jamais appliquée, faute de conteneur déclaré : la règle était
   * morte et le débordement intact.</p>
   *
   * <p>Trois paliers : l'heure et le titre tiennent partout ; la matière
   * apparaît à partir de trois quarts d'heure ; le destinataire à partir d'une
   * heure et demie. Ce que la pastille tait figure au panneau de détail.</p>
   */
  private renderEvent(s: AcademicAppointment) {
    const time = displayTime(s.startTime);
    const target = s.groupName ?? (s.students?.length ? `${s.students.length}` : '');
    const subject = this.view.subjectLabel(s);

    const minutes = s.durationMinutes ?? 0;
    const roomy = minutes >= 45;
    const spacious = minutes >= 90;

    const html = `
      <div class="sess-pill">
        <span class="sess-pill__head">
          <span class="sess-pill__time">${escapeHtml(time)}</span>
          <span class="sess-pill__title">${escapeHtml(s.title)}</span>
        </span>
        ${roomy && subject ? `<span class="sess-pill__meta">${escapeHtml(subject)}</span>` : ''}
        ${spacious && target ? `<span class="sess-pill__meta sess-pill__meta--target">${escapeHtml(target)}</span>` : ''}
      </div>`;

    return { html };
  }

  // ─── Filtres ───────────────────────────────────────────────────────

  applyFilters(): void {
    this.api()?.refetchEvents();
  }

  clearFilters(): void {
    this.filterSubjectId = null;
    this.filterGroupId = null;
    this.filterStudentId = null;
    this.applyFilters();
  }

  // ─── Formulaire ────────────────────────────────────────────────────

  /** Le bouton de création ne dépend d'aucun filtre : il ouvre toujours un formulaire vierge. */
  openCreate(): void {
    this.form = EMPTY_FORM();
    this.form.date = new Date().toISOString().slice(0, 10);
    this.prefillFromFilters();
    this.editingId.set(null);
    this.studentQuery.set('');
    this.onSubjectChange();
    // Une création part toujours du cas courant — une séance suivie par des
    // élèves. L'enregistrement est un choix, pas l'état par défaut d'un
    // formulaire vierge.
    this.audienceMode.set('students');
    this.syncTargetCount();
    this.openFormDialog();
  }

  private openCreateAt(arg: DateSelectArg): void {
    this.form = EMPTY_FORM();
    this.form.date = arg.startStr.slice(0, 10);
    // Sélection dans une vue horaire : on reprend l'heure cliquée.
    if (arg.startStr.length > 10) this.form.startTime = arg.startStr.slice(11, 16);
    this.prefillFromFilters();
    this.editingId.set(null);
    this.studentQuery.set('');
    this.onSubjectChange();
    // Une création part toujours du cas courant — une séance suivie par des
    // élèves. L'enregistrement est un choix, pas l'état par défaut d'un
    // formulaire vierge.
    this.audienceMode.set('students');
    this.syncTargetCount();
    this.openFormDialog();
    this.api()?.unselect();
  }

  /** Un filtre actif est une intention : on le reprend comme valeur de départ. */
  private prefillFromFilters(): void {
    if (this.filterSubjectId) this.form.subjectIds = [this.filterSubjectId];
    if (this.filterGroupId) this.form.groupId = this.filterGroupId;
    if (this.filterStudentId) this.form.studentIds = [this.filterStudentId];
  }

  openEdit(s: AcademicAppointment): void {
    this.form = {
      title: s.title,
      kind: s.sessionKind ?? 'Course',
      // Repli sur la principale pour les séances antérieures au multi-matières,
      // qu'aucune reprise côté client ne peut avoir enrichies.
      subjectIds: s.subjects?.length
        ? s.subjects.map((x) => x.id)
        : s.subjectId
          ? [s.subjectId]
          : [],
      groupId: s.groupId ?? null,
      studentIds: (s.students ?? []).map((x) => x.studentId),
      date: (s.scheduledDate ?? '').slice(0, 10),
      startTime: displayTime(s.startTime),
      durationMinutes: s.durationMinutes ?? DEFAULT_DURATION_MINUTES,
      location: s.location ?? '',
      meetingUrl: s.meetingUrl ?? '',
      reason: s.reason ?? '',
    };
    this.editingId.set(s.id);
    this.studentQuery.set('');
    this.onSubjectChange();
    this.syncAudienceFromForm();
    this.closeDetail();
    this.openFormDialog();
  }

  /** Relit l'intention depuis les données : pas de cible ⇒ séance d'enregistrement. */
  private syncAudienceFromForm(): void {
    const hasAudience = this.form.groupId !== null || this.form.studentIds.length > 0;
    this.audienceMode.set(hasAudience ? 'students' : 'recording');
    this.syncTargetCount();
  }

  // ─── Panneaux natifs ───────────────────────────────────────────────

  private openFormDialog(): void {
    // Un formulaire qui s'ouvre ne porte ni faute ni echec : ceux de la
    // tentative precedente n'ont plus de sujet.
    this.submitted.set(false);
    this.formError.set(null);

    this.showForm.set(true);
    // showModal() est idempotent tant que le dialogue est déjà ouvert, mais
    // rappelé sur un dialogue ouvert il lève : on s'en prémunit.
    const el = this.formDialog?.nativeElement;
    if (el && !el.open) el.showModal();
  }

  closeForm(): void {
    if (this.saving()) return;
    this.formDialog?.nativeElement.close();
  }

  /** Reflète dans l'état la fermeture faite par le navigateur (Échap, close()). */
  onFormClosed(): void {
    this.showForm.set(false);
    // Un enregistrement interrompu ne doit pas laisser le bouton désactivé à la
    // réouverture du formulaire.
    this.saving.set(false);
  }

  /**
   * Refuse la fermeture pendant l'enregistrement. `cancel` est annulable :
   * c'est le seul moyen d'intercepter Échap, qui abandonnerait sinon la saisie
   * alors que la requête est encore en vol.
   */
  onFormCancel(event: Event): void {
    if (this.saving()) event.preventDefault();
  }

  /** Cible du `mousedown` en cours — voir `onBackdropClick`. */
  private pressOrigin: EventTarget | null = null;

  onPressStart(event: MouseEvent): void {
    this.pressOrigin = event.target;
  }

  /**
   * Un clic sur le fond d'un `<dialog>` a pour cible le dialogue lui-même : le
   * contenu est un enfant, donc tout clic « à côté » remonte ici avec
   * `target === dialog`. C'est la façon canonique de fermer au clic extérieur
   * sans voile maison.
   *
   * On exige que le geste ait **commencé** sur le fond : sans cela, une
   * sélection de texte démarrée dans le formulaire et relâchée en dehors
   * fermerait le panneau et perdrait la saisie.
   */
  onBackdropClick(event: MouseEvent, dialog: HTMLDialogElement): void {
    const startedOnBackdrop = this.pressOrigin === dialog || this.pressOrigin === null;
    this.pressOrigin = null;
    if (event.target !== dialog || !startedOnBackdrop) return;

    // Même garde que la croix et qu'Échap : ni un dépôt ni un enregistrement en
    // cours ne se ferment par un clic à côté.
    if (dialog === this.detailDialog?.nativeElement && this.uploadBusy()) return;
    if (dialog === this.formDialog?.nativeElement && this.saving()) return;

    dialog.close();
  }

  /**
   * Refuse la fermeture pendant un transfert. Le `cancel` d'un `<dialog>` est
   * annulable : c'est le seul moyen d'intercepter la touche Échap, qui
   * abandonnerait un envoi de plusieurs gigaoctets déjà bien avancé.
   */
  onDetailCancel(event: Event): void {
    if (this.uploadBusy()) event.preventDefault();
  }

  /** Ouvre le tiroir et charge en parallèle le bilan de présence. */
  openDetail(s: AcademicAppointment): void {
    this.detailError.set(null);
    this.selected.set(s);
    this.attendance.set(null);
    this.resetOutcomeState();

    const el = this.detailDialog?.nativeElement;
    if (el && !el.open) el.showModal();

    // Sans réunion en ligne, le journal d'accès n'a rien à dire.
    if (!s.meetingUrl) return;

    this.attendanceLoading.set(true);
    this.joinService.myAttendance(s.id).subscribe({
      next: (res) => {
        // Le tiroir a pu changer de séance pendant la requête.
        if (this.selected()?.id === s.id) this.attendance.set(res?.data ?? null);
        this.attendanceLoading.set(false);
      },
      error: () => {
        this.attendance.set(null);
        this.attendanceLoading.set(false);
      },
    });
  }

  closeDetail(): void {
    if (this.uploadBusy()) return;
    this.detailDialog?.nativeElement.close();
  }

  onDetailClosed(): void {
    this.selected.set(null);
    this.attendance.set(null);
    this.resetOutcomeState();
  }

  private resetOutcomeState(): void {
    this.outcomeError.set(null);
    this.outcomeSaving.set(false);
    this.declining.set(false);
    this.declineReason = '';
    this.uploadOpen.set(false);
    this.uploadError.set(null);
    this.uploadPercent.set(null);
    this.uploadBusy.set(false);
    this.recordingFile = null;
    this.recordingTitle = '';
    this.pendingUpload = null;
  }

  /** L'accès du prof est journalisé comme celui des élèves. */
  join(s: AcademicAppointment): void {
    this.detailError.set(null);
    this.joinService.openMeeting(s.id, (err) =>
      this.detailError.set(this.messageFrom(err)),
    );
  }

  verdictIcon(verdict: AttendanceVerdict): string {
    switch (verdict) {
      case 'Held':      return 'fa-solid fa-circle-check';
      case 'Disputed':  return 'fa-solid fa-triangle-exclamation';
      case 'NotHeld':   return 'fa-solid fa-circle-xmark';
      case 'Pending':   return 'fa-solid fa-hourglass-half';
      case 'Cancelled': return 'fa-solid fa-ban';
      default:          return 'fa-solid fa-location-dot';
    }
  }

  /** Le changement de matières restreint la liste d'élèves proposés. */
  onSubjectChange(): void {
    this.formSubjectIds.set([...this.form.subjectIds]);
  }

  /**
   * Coche ou décoche une matière.
   *
   * <p>La première cochée reste en tête et demeure la principale : décocher
   * puis recocher une matière la renvoie en fin de liste, ce qui est le
   * comportement attendu — c'est bien une nouvelle désignation.</p>
   */
  toggleSubject(id: number): void {
    const list = this.form.subjectIds;
    this.form.subjectIds = list.includes(id)
      ? list.filter((x) => x !== id)
      : [...list, id];
    this.onSubjectChange();
  }

  isSubjectPicked(id: number): boolean {
    return this.form.subjectIds.includes(id);
  }

  /**
   * Matière principale du formulaire — celle qui donne sa couleur à la séance.
   * Signalée à l'écran : sans cela, l'ordre des cases n'aurait aucun effet
   * visible et le professeur ne pourrait pas choisir la teinte.
   */
  isPrincipalSubject(id: number): boolean {
    return this.form.subjectIds[0] === id;
  }

  toggleStudent(id: number): void {
    const list = this.form.studentIds;
    this.form.studentIds = list.includes(id)
      ? list.filter((x) => x !== id)
      : [...list, id];
    this.syncTargetCount();
  }

  /**
   * Bascule entre « des élèves suivent la séance » et « créneau d'enregistrement ».
   * Passer en enregistrement vide la cible : c'est ce vide qui, côté serveur,
   * définit une séance de production.
   */
  setAudience(mode: 'students' | 'recording'): void {
    this.audienceMode.set(mode);
    if (mode === 'recording') {
      this.form.groupId = null;
      this.form.studentIds = [];
    }
    this.syncTargetCount();
  }

  onGroupChange(): void {
    this.syncTargetCount();
  }

  private syncTargetCount(): void {
    const group = this.options().groups.find((g) => g.id === this.form.groupId);
    this.formTargetCount.set((group?.memberCount ?? 0) + this.form.studentIds.length);
  }

  /**
   * Fin du créneau — **modifiable**.
   *
   * <p>Elle est dérivée du début et de la durée, mais la saisir revient à régler
   * la durée : un professeur raisonne aussi bien en « jusqu'à 10h15 » qu'en
   * « 45 minutes », selon qu'il cale une séance entre deux autres ou qu'il pose
   * un cours standard. Les deux commandes agissent sur la même valeur, celle
   * que l'API attend.</p>
   */
  get endTime(): string {
    const start = toMinutes(this.form.startTime);
    if (start === null) return '';
    return fromMinutes(start + this.form.durationMinutes);
  }

  set endTime(value: string) {
    const minutes = this.durationUntil(value);
    // Une fin antérieure au début, ou hors des bornes acceptées par l'API, est
    // une faute de frappe : on conserve la durée en place plutôt que d'envoyer
    // un créneau que le serveur refusera.
    if (minutes !== null) this.form.durationMinutes = minutes;
  }

  /** Durée impliquée par une heure de fin, ou `null` si elle n'est pas tenable. */
  private durationUntil(end: string): number | null {
    const from = toMinutes(this.form.startTime);
    const to = toMinutes(end);
    if (from === null || to === null) return null;

    const minutes = to - from;
    if (minutes < MIN_DURATION_MINUTES || minutes > MAX_DURATION_MINUTES) return null;
    return minutes;
  }

  /** La durée courante figure-t-elle parmi les segments proposés ? */
  get isPresetDuration(): boolean {
    return DURATIONS.includes(this.form.durationMinutes);
  }

  isStudentPicked(id: number): boolean {
    return this.form.studentIds.includes(id);
  }

  /**
   * Le serveur accepte une séance sans cible — c'est ce qui définit une séance
   * de production. Mais ici l'intention est explicite : en mode « des élèves
   * suivent », une cible vide est un oubli, pas un choix. Sans ce garde-fou, la
   * séance partirait en production et disparaîtrait de tous les agendas
   * d'élèves sans que personne ne l'ait demandé.
   */
  get formValid(): boolean {
    const targeted =
      this.audienceMode() === 'recording' ||
      this.form.groupId !== null ||
      this.form.studentIds.length > 0;

    return (
      !!this.form.title.trim() &&
      this.form.subjectIds.length > 0 &&
      !!this.form.date &&
      !!this.form.startTime &&
      targeted
    );
  }


  submit(): void {
    if (this.saving()) return;

    // La tentative est enregistree meme quand elle echoue a la validation :
    // c'est elle qui autorise les champs a se signaler en faute.
    this.submitted.set(true);
    this.formError.set(null);

    if (!this.formValid) {
      this.formError.set(this.translate.instant('CALENDAR.CREATE.FIX_FIELDS'));
      return;
    }

    this.saving.set(true);

    const id = this.editingId();
    const request = id === null
      ? this.service.create(this.toCreateDto())
      : this.service.update(id, this.toUpdateDto());

    request.subscribe({
      next: () => {
        this.saving.set(false);
        this.closeForm();
        this.api()?.refetchEvents();
      },
      error: (err) => {
        this.saving.set(false);
        this.formError.set(this.messageFrom(err));
      },
    });
  }

  cancelSession(s: AcademicAppointment): void {
    // Le motif part aux élèves dans la notification d'annulation : il est requis.
    const reason = window.prompt(this.translate.instant('CALENDAR.CANCEL_REASON'));
    if (!reason?.trim()) return;

    this.service.cancel(s.id, reason.trim()).subscribe({
      next: () => {
        this.closeDetail();
        this.api()?.refetchEvents();
      },
      error: (err) => this.detailError.set(this.messageFrom(err)),
    });
  }

  // ─── Issue d'une séance de production ──────────────────────────────

  /**
   * L'issue ne se déclare que sur une séance sans destinataire et déjà
   * commencée. Ailleurs, c'est le journal d'accès qui atteste la tenue, et une
   * déclaration viendrait concurrencer une preuve.
   */
  canDeclare(s: AcademicAppointment): boolean {
    if (!s.isProduction || s.status === 'Cancelled') return false;

    // Le serveur lit la date et l'heure stockées comme un instant UTC (même
    // convention que le planificateur de rappels). Sans le « Z », le navigateur
    // les interpréterait en heure locale et proposerait les boutons une heure
    // trop tôt en Tunisie — pour se faire refuser par l'API.
    return Date.parse(`${sessionStart(s)}Z`) <= Date.now();
  }

  declare(s: AcademicAppointment, held: boolean): void {
    // « Non tenue » exige un motif : on ouvre la saisie avant d'envoyer.
    if (!held && !this.declining()) {
      this.declining.set(true);
      return;
    }

    if (this.outcomeSaving()) return;
    this.outcomeSaving.set(true);
    this.outcomeError.set(null);

    this.service.declareOutcome(s.id, held, held ? null : this.declineReason).subscribe({
      next: (res) => {
        this.outcomeSaving.set(false);
        this.declining.set(false);
        this.declineReason = '';
        if (res?.data) this.selected.set(res.data);
        this.api()?.refetchEvents();
      },
      error: (err) => {
        this.outcomeSaving.set(false);
        this.outcomeError.set(this.messageFrom(err));
      },
    });
  }

  cancelDecline(): void {
    this.declining.set(false);
    this.declineReason = '';
    this.outcomeError.set(null);
  }

  // ─── Enregistrement de la séance ───────────────────────────────────

  openUpload(s: AcademicAppointment): void {
    this.uploadOpen.set(true);
    this.uploadError.set(null);
    this.uploadPercent.set(null);
    this.recordingFile = null;
    // Par défaut, la vidéo porte le nom de la séance : c'est sous ce titre que
    // les élèves la retrouveront dans la bibliothèque.
    this.recordingTitle = s.title;
  }

  closeUpload(): void {
    if (this.uploadBusy()) return;
    this.uploadOpen.set(false);
    this.uploadError.set(null);
  }

  onRecordingPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.recordingFile = input.files?.[0] ?? null;
    this.uploadError.set(null);
    // Le dépôt en attente concernait le fichier précédent : le garder ferait
    // rattacher l'ancienne vidéo sous le nouveau titre, et la nouvelle ne
    // partirait jamais.
    this.pendingUpload = null;
  }

  get recordingValid(): boolean {
    return !!this.recordingFile && !!this.recordingTitle.trim();
  }

  /**
   * Dépose la vidéo puis la rattache à la séance.
   *
   * Le fichier part directement vers le stockage : à 2 Go, le faire transiter
   * par l'API mobiliserait une connexion serveur et son disque temporaire
   * pendant tout le transfert. L'API ne voit ensuite que la clé.
   */
  uploadRecording(s: AcademicAppointment): void {
    if (!this.recordingValid || this.uploadBusy()) return;

    // La séance porte déjà le périmètre : la vidéo hérite de sa matière et du
    // parcours de celle-ci, sans quoi elle atterrirait dans la bibliothèque
    // sans classement. Un parcours introuvable serait interprété par le serveur
    // comme « diffuser à tous les parcours » : on refuse plutôt que de publier
    // à tout l'établissement un enregistrement destiné à une matière.
    const learningPathId = this.learningPathOf(s.subjectId ?? null);
    if (learningPathId === null) {
      this.uploadError.set(this.translate.instant('CALENDAR.RECORDING.NO_SCOPE'));
      return;
    }

    // Le formulaire est figé ici : le tiroir peut se refermer pendant un
    // transfert de plusieurs minutes, et l'enregistrement final ne doit pas
    // dépendre d'un champ entre-temps réinitialisé.
    const payload = {
      title: this.recordingTitle.trim(),
      learningPathId,
      subjectId: s.subjectId ?? null,
      isPublic: true,
      appointmentId: s.id,
    };

    this.uploadBusy.set(true);
    this.uploadError.set(null);

    // Le transfert a déjà abouti lors d'une tentative précédente : seul le
    // rattachement reste à refaire.
    if (this.pendingUpload) {
      this.registerRecording(payload, this.pendingUpload);
      return;
    }

    this.uploadPercent.set(0);

    this.uploads.uploadDirect(this.recordingFile!, UploadPurpose.CourseMaterial).subscribe({
      next: (progress) => {
        this.uploadPercent.set(progress.percent);
        if (!progress.done || !progress.result) return;

        this.pendingUpload = progress.result;
        this.registerRecording(payload, progress.result);
      },
      error: (err) => {
        this.uploadBusy.set(false);
        this.uploadPercent.set(null);
        this.uploadError.set(this.messageFrom(err));
      },
    });
  }

  /** Second temps du dépôt : rattacher l'objet déjà stocké à la bibliothèque. */
  private registerRecording(
    payload: {
      title: string;
      learningPathId: number;
      subjectId: number | null;
      isPublic: boolean;
      appointmentId: number;
    },
    uploaded: UploadResult,
  ): void {
    this.materials
      .register({
        ...payload,
        objectKey: uploaded.key,
        fileName: uploaded.originalFileName,
        contentType: uploaded.contentType,
        sizeBytes: uploaded.sizeBytes,
      })
      .subscribe({
        next: () => {
          this.pendingUpload = null;
          this.uploadBusy.set(false);
          this.uploadOpen.set(false);
          this.uploadPercent.set(null);
          this.recordingFile = null;

          // Deux rafraîchissements, et non un seul. Le tiroir relit la séance
          // pour afficher l'enregistrement tout de suite ; la grille relit ses
          // événements parce que c'est *elle* qui fournit la séance quand on la
          // rouvre. Sans le second, refermer puis rouvrir la séance ressortait
          // la copie mise en cache par le calendrier — sans la vidéo — et il
          // fallait recharger la page pour la voir apparaître.
          this.refreshSelected(payload.appointmentId);
          this.api()?.refetchEvents();
        },
        error: (err) => {
          this.uploadBusy.set(false);
          this.uploadError.set(this.messageFrom(err));
        },
      });
  }

  private refreshSelected(id: number): void {
    this.service.getById(id).subscribe({
      next: (res) => {
        if (res?.data && this.selected()?.id === id) this.selected.set(res.data);
      },
      // Échec sans conséquence : le tiroir garde la version qu'il affichait,
      // le dépôt lui a déjà réussi.
      error: () => {},
    });
  }

  /** Parcours auquel appartient une matière du référentiel de planification. */
  private learningPathOf(subjectId: number | null): number | null {
    if (!subjectId) return null;
    return this.options().subjects.find((s) => s.id === subjectId)?.learningPathId ?? null;
  }

  /** Taille lisible d'un enregistrement. */
  fileSize(bytes: number | null | undefined): string {
    if (!bytes) return '';
    const units = ['o', 'Ko', 'Mo', 'Go'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit++;
    }
    return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
  }

  private toCreateDto(): CreateAcademicAppointmentDto {
    return {
      // Les deux voyagent : la principale pour ce qui n'en lit qu'une, la liste
      // pour le reste. Le serveur reconstruit la principale depuis la liste, les
      // deux ne peuvent donc pas se contredire.
      subjectId: this.form.subjectIds[0],
      subjectIds: this.form.subjectIds,
      groupId: this.form.groupId,
      studentIds: this.form.studentIds,
      sessionKind: SESSION_KIND_VALUE[this.form.kind],
      title: this.form.title.trim(),
      reason: this.form.reason.trim() || null,
      scheduledDate: this.form.date,
      startTime: `${this.form.startTime}:00`,
      durationMinutes: this.form.durationMinutes,
      location: this.form.location.trim() || null,
      meetingUrl: this.form.meetingUrl.trim() || null,
    };
  }

  private toUpdateDto(): UpdateAcademicAppointmentDto {
    return {
      subjectId: this.form.subjectIds[0] ?? null,
      subjectIds: this.form.subjectIds,
      groupId: this.form.groupId,
      clearGroup: this.form.groupId === null,
      studentIds: this.form.studentIds,
      sessionKind: SESSION_KIND_VALUE[this.form.kind],
      title: this.form.title.trim(),
      reason: this.form.reason.trim(),
      scheduledDate: this.form.date,
      startTime: `${this.form.startTime}:00`,
      durationMinutes: this.form.durationMinutes,
      location: this.form.location.trim(),
      meetingUrl: this.form.meetingUrl.trim(),
    };
  }

  /**
   * Message d'erreur dans la langue de l'utilisateur.
   *
   * Le serveur envoie un `code` stable (`SESSION_SLOT_TAKEN`…) accompagné de ses
   * paramètres, et un `message` rédigé en français. On traduit le code ; le
   * message français ne sert que de repli, pour un code que le front ne connaît
   * pas encore — mieux vaut une phrase dans la mauvaise langue que rien.
   */
  private messageFrom(err: unknown): string {
    const body = (err as {
      error?: { code?: string; params?: Record<string, string>; message?: string };
    })?.error;

    if (body?.code) {
      const key = `CALENDAR.ERRORS.${body.code}`;
      const translated = this.translate.instant(key, body.params ?? {});
      // ngx-translate rend la clé telle quelle quand elle est absente.
      if (translated !== key) return translated;
    }

    return body?.message || this.translate.instant('CALENDAR.SAVE_ERROR');
  }

  /**
   * Erreur d'enregistrement du formulaire, affichée en entête de la modale.
   *
   * <p>Une alerte flottante s'ouvrait ici auparavant. Peinte dans la couche
   * supérieure, elle recouvrait la modale sans jamais recevoir le clic : il
   * fallait fermer la modale — donc perdre la saisie — pour s'en débarrasser.
   * Le message vit désormais dans la modale, à l'endroit que l'œil regarde en
   * premier après avoir cliqué sur « enregistrer ».</p>
   */
  readonly formError = signal<string | null>(null);

  /** Erreur d'une action du tiroir de détail (rejoindre, annuler). */
  readonly detailError = signal<string | null>(null);

  /**
   * Une tentative d'enregistrement a eu lieu.
   *
   * <p>Les champs ne se signalent en faute qu'à partir de là : marquer en rouge
   * un formulaire qu'on vient d'ouvrir reproche à l'utilisateur de ne pas avoir
   * encore saisi ce qu'il s'apprête à saisir.</p>
   */
  readonly submitted = signal(false);

  titleInvalid(): boolean {
    return this.submitted() && !this.form.title.trim();
  }

  subjectsInvalid(): boolean {
    return this.submitted() && this.form.subjectIds.length === 0;
  }

  dateInvalid(): boolean {
    return this.submitted() && !this.form.date;
  }

  startInvalid(): boolean {
    return this.submitted() && !this.form.startTime;
  }

  /** Une séance suivie vise un groupe ou des élèves ; un enregistrement, personne. */
  audienceInvalid(): boolean {
    return (
      this.submitted() &&
      this.audienceMode() === 'students' &&
      this.form.groupId === null &&
      this.form.studentIds.length === 0
    );
  }

  // ─── Helpers de gabarit ────────────────────────────────────────────

  time(value: string | null | undefined): string {
    return displayTime(value);
  }

  start(s: AcademicAppointment): string {
    return sessionStart(s);
  }

  /** Libellé d'une matière, situé par son parcours si son nom est ambigu. */
  subjectLabel(s: SchedulingSubject): string {
    return this.subjectChoice.label(s, this.options().subjects);
  }

  /** Matières du formulaire, regroupées par parcours. */
  readonly subjectGroups = computed<SubjectPathGroup[]>(() => {
    this.lang();
    return this.subjectChoice.groupByPath(this.options().subjects);
  });

  subjectName(id: number | null): string {
    const s = this.options().subjects.find((x) => x.id === id);
    return s ? this.labels.pick(s) : '';
  }

  groupName(id: number | null): string {
    return this.options().groups.find((g) => g.id === id)?.name ?? '';
  }
}

/**
 * "HH:mm" → minutes depuis minuit, ou `null` si la valeur n'est pas une heure.
 *
 * L'analyse est stricte à dessein : un champ `type="time"` vidé rend la chaîne
 * vide, et un découpage naïf (`''.split(':').map(Number)`) donne `[0]` — donc
 * une minute `undefined` que `Number.isNaN` ne rattrape pas. On propageait
 * alors un `NaN` jusqu'à la durée envoyée à l'API.
 */
function toMinutes(hhmm: string | null | undefined): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec((hhmm ?? '').trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

/** Minutes depuis minuit → "HH:mm", en repassant par 00:00 après minuit. */
function fromMinutes(total: number): string {
  const wrapped = ((total % 1440) + 1440) % 1440;
  const hh = String(Math.floor(wrapped / 60)).padStart(2, '0');
  const mm = String(wrapped % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Le contenu d'événement est injecté en HTML : titres et noms doivent être neutralisés. */
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
